/* eslint-disable @typescript-eslint/no-base-to-string */
import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useMemo, useState} from "react";
import type {RowSelectionState} from "@tanstack/react-table";
import {useMediaQuery} from "@mantine/hooks";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {OttopilotColumnType} from "~/components/ottopilotDataViewer/column";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {
    orderedVisibleColumnIdsWithActionsLast,
    TABLE_ACTIONS_COLUMN_ID,
} from "~/components/ottopilotDataViewer/columnConfiguration";
import type {OttopilotView, ViewType} from "~/components/ottopilotDataViewer/view";
import {clearAllDataViewerFilters} from "~/components/ottopilotDataViewer/dataViewerFilterActions";
import {useTableRows} from "~/components/ottopilotDataViewer/viewDataHooks";
import {AnankeSimpleTag} from "@vesta/ananke/components/anankeSimpleTag";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useEventContainerTemplates} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {
    archiveDataViewerRow,
    EVENTS_MODULE_DATA_VIEWER_QUERY_KEY,
    isEventDataViewerRow,
} from "~/utilities/archiveEventContainerWithEvents";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {getRandomTagColor} from "~/utilities/utilities";
import {twJoin} from "tailwind-merge";
import {DhiraaguEventTypes} from "~/domainModels/workspaceSpecific/dhiraagu";
import {GenericTable} from "~/components/table/GenericTable";
import {TableDeleteConfirmationModal} from "~/components/table/TableDeleteConfirmationModal";
import {TableFooter} from "~/components/table/TableFooter";
import {TableLoadErrorEmptyState} from "~/components/table/TableLoadErrorEmptyState";
import {TableNoSearchResultsEmptyState} from "~/components/table/TableNoSearchResultsEmptyState";
import {TableOperationToasts} from "~/components/table/TableOperationToasts";
import {TableSelectionBar} from "~/components/table/TableSelectionBar";
import type {TableColumnDef, TableConfig} from "~/components/table/table.types";
import "~/components/table/table.css";

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

export function GenericTableView<
    T extends {
        id: Uuid;
    },
>({
    controller,
    view,
    hidePageFooter = false,
    horizontalInsetClass = "px-ds-space-200 sm:px-ds-space-300",
}: {
    controller: OttopilotDataViewerController<T>;
    view: OttopilotView<T> & {
        viewType: ViewType.table;
    };
    hidePageFooter?: boolean;
    horizontalInsetClass?: string;
}) {
    const {workspace, workspaceCapabilities} = useWorkspace();
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {showFlag} = useAnankeFlags();
    const queryClient = useQueryClient();
    const eventContainerTemplates = useEventContainerTemplates();
    const eventTemplates = useEventTemplates();
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const rowsQuery = useTableRows(controller);
    // Infinite: rowsQuery.data.pages.flatMap((page: Array<T>) => page)
    const loadedDataFromQuery: Array<T> | null =
        rowsQuery.status === "success" ? rowsQuery.data : null;
    const isTableDataLoading =
        rowsQuery.status === "pending" ||
        (rowsQuery.isFetching === true && loadedDataFromQuery === null);
    const isDhiraaguEnquiry = controller.queryKeyPrefix.includes(DhiraaguEventTypes.enquiry);
    let hasAnyEditableRow = true;
    if (controller.canEditRow !== undefined && loadedDataFromQuery !== null) {
        hasAnyEditableRow = loadedDataFromQuery.some((row: T) => controller.canEditRow?.(row) === true);
    }
    const showEditActionPerRow = controller.showEditActionPerRow === true;
    const hasReadPanel = controller.readPanelComponent !== undefined;
    const hasAdditionalActionComponent = controller.additionalActionComponent !== undefined;
    const hasTableRowActions = controller.tableRowActions !== undefined;
    const hasUpdatePanel =
        (showEditActionPerRow || hasAnyEditableRow) &&
        controller.updatePanelComponent !== undefined &&
        !isDhiraaguEnquiry;
    const rawOrderedVisibleColumnIds =
        controller.tableColumnConfig !== null
            ? controller.tableColumnConfig.orderedVisibleColumnIds
            : view.visibleColumns;
    const showActionsColumn =
        (hasReadPanel || hasUpdatePanel || hasAdditionalActionComponent || hasTableRowActions) &&
        rawOrderedVisibleColumnIds.includes(TABLE_ACTIONS_COLUMN_ID);
    const activeColumnIdsForTable = orderedVisibleColumnIdsWithActionsLast(
        rawOrderedVisibleColumnIds,
        showActionsColumn,
    );
    const hasVisibleDataColumns = activeColumnIdsForTable.some(
        (columnId) => columnId !== TABLE_ACTIONS_COLUMN_ID,
    );
    const enableTableRowSelection = controller.enableTableRowSelection !== false;

    const columns = useMemo<Array<TableColumnDef<T>>>(() => {
        const activeColumnIds = activeColumnIdsForTable;

        const pinnedLeftIds = new Set(controller.tableColumnConfig?.pinnedLeftColumnIds ?? []);

        const columns: Array<TableColumnDef<T>> = [];
        for (const columnId of activeColumnIds) {
            const column = controller.columns.lineItems.find((column_) => column_.id === columnId);
            if (column === undefined) {
                console.log(`Ignoring invalid column ${columnId}`);
                continue;
            }

            // Handle checkbox column for row selection
            if (columnId === "__checkbox__" && view.otherArgs?.rowSelection === "multiple") {
                continue;
            }

            const colDef: TableColumnDef<T> = {
                id: column.id,
                displayName: column.displayName,
                pinned: pinnedLeftIds.has(columnId) ? "left" : column.pinned,
                valueGetter: (item) => {

                    // Check if this is a skeleton row (id starts with 'skeleton-')
                    if ("id" in item && typeof item.id === "string" && item.id.startsWith("skeleton-")) {
                        return undefined;
                    }

                    try {
                        const value = column.valueGetter(item);

                        // Skip valueFormatter here for array types - it's handled in cellRenderer
                        if (
                            column.type !== OttopilotColumnType.array &&
                            column.valueFormatter !== undefined &&
                            value !== null &&
                            value !== undefined
                        ) {
                            const formatter = column.valueFormatter as (v: unknown) => string;
                            return formatter(value) ?? undefined;
                        }

                        return value ?? undefined;
                    } catch {
                        // If valueGetter fails (e.g., missing properties), return undefined
                        return undefined;
                    }
                },
                width: 180,
                // sortable: column.id === "__actions__" ? false : Boolean(column.sortingCapability),
                sortable: false,
            };

            if (column.id === "__actions__") {
                colDef.type = "actions";
                colDef.resizable = false;
                colDef.width = hasTableRowActions ? 152 : 180;
                colDef.minWidth = hasTableRowActions ? 140 : 72;
            }

            if (column.cellRenderer !== undefined) {
                colDef.cellRenderer = (row) => column.cellRenderer!(row);
            } else if (column.type === OttopilotColumnType.array) {
                colDef.cellRenderer = (row) => {
                    const arrayValue = column.valueGetter(row);
                    const formattedValues =
                        column.valueFormatter !== undefined ? column.valueFormatter(arrayValue) : arrayValue;
                    return (
                        <div className="grid grid-flow-col justify-start [&_span]:text-[14px]">
                            {formattedValues.map((formattedText: string, index: number) => (
                                <AnankeSimpleTag
                                    text={formattedText}
                                    key={index}
                                    color={getRandomTagColor(index)}
                                />
                            ))}
                        </div>
                    );
                };
            }

            columns.push(colDef);
        }

        return columns;
    }, [
        controller.columns.revisionId,
        activeColumnIdsForTable,
        controller.columns.lineItems,
        controller.tableColumnConfig,
        view.otherArgs?.rowSelection,
        hasTableRowActions,
    ]);

    const pageSize = controller.pageSize > 0 ? controller.pageSize : 50;
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [showCopyToast, setShowCopyToast] = useState(false);
    const [isDeletingSelectedRows, setIsDeletingSelectedRows] = useState(false);
    const [optimisticallyDeletedRowIds, setOptimisticallyDeletedRowIds] = useState<Array<string>>([]);
    const [hasPendingDeleteToast, setHasPendingDeleteToast] = useState(false);
    const [pendingDeletePayload, setPendingDeletePayload] = useState<{rowsToDelete: Array<T>; rowIdsToDelete: Array<string>} | null>(null);
    const [pendingDeleteTimeoutId, setPendingDeleteTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
    const loadedDataRaw: Array<T> = loadedDataFromQuery ?? [];
    const loadedData = useMemo(
        () => loadedDataRaw.filter((row: T) => !optimisticallyDeletedRowIds.includes(String(row.id))),
        [loadedDataRaw, optimisticallyDeletedRowIds],
    );
    // Empty pending data so GenericTable renders `createSkeletonRows` (distinct row ids via `__skeletonIndex__`).
    const tableRowData = useMemo(() => {
        if (isTableDataLoading === true && loadedDataFromQuery === null) {
            return [];
        }
        return loadedData;
    }, [isTableDataLoading, loadedDataFromQuery, loadedData]);
    const totalRowsRaw = controller.rowCountQuery.status === "success" ? controller.rowCountQuery.data : null;
    const totalRows =
        totalRowsRaw === null ? loadedData.length : Math.max(loadedData.length, totalRowsRaw - optimisticallyDeletedRowIds.length);
    const currentPage = controller.pageNumber > 0 ? controller.pageNumber : 1;
    const totalPages =
        totalRowsRaw !== null && pageSize > 0
            ? Math.max(1, Math.ceil(Math.max(0, totalRowsRaw - optimisticallyDeletedRowIds.length) / pageSize))
            : 1;
    const tableRowSelectionState = useMemo<RowSelectionState>(
        () => Object.fromEntries(controller.tableSelectedRowIds.map((id) => [id, true] as const)),
        [controller.tableSelectedRowIds],
    );

    useEffect(() => {
        if (
            (hasVisibleDataColumns === false || enableTableRowSelection !== true) &&
            controller.tableSelectedRowIds.length > 0
        ) {
            controller.setTableSelectedRowIds([]);
        }
    }, [
        hasVisibleDataColumns,
        enableTableRowSelection,
        controller.tableSelectedRowIds.length,
        controller.setTableSelectedRowIds,
    ]);

    const handleClearFilters = useCallback(() => {
        clearAllDataViewerFilters(controller);
    }, [controller]);

    const handleRetryLoadData = useCallback(() => {
        void rowsQuery.refetch();
    }, [rowsQuery]);

    /** Table card node — empty state overlay matches this element’s screen rect (see TableNoSearchResultsEmptyState). */
    const [tablePanelBoundsElement, setTablePanelBoundsElement] = useState<HTMLDivElement | null>(null);

    const isFullscreenEngagementPanelOpen =
        controller.currentlyOpenRowEngagementPanel.rowEngagementType !== RowEngagementType.none
        && controller.currentlyOpenRowEngagementPanel.rowEngagementPanelType === RowEngagementPanelType.fullscreen;

    const emptyState = useMemo(() => {
        // Empty states portal into the table panel (absolute) so hidden tabs do not leave a stale
        // fixed overlay; suppressed while a fullscreen engagement panel is open.
        if (isFullscreenEngagementPanelOpen === true) {
            return null;
        }

        const showLoadError = !isTableDataLoading && loadedData.length === 0 && rowsQuery.status === "error";

        if (showLoadError) {
            return (
                <TableLoadErrorEmptyState
                    onRetry={handleRetryLoadData}
                    viewportBoundsElement={tablePanelBoundsElement}
                />
            );
        }

        const showDataEmpty =
            !isTableDataLoading && loadedData.length === 0 && rowsQuery.status === "success";

        if (showDataEmpty) {
            if (controller.renderTableNoDataEmptyState !== undefined) {
                return controller.renderTableNoDataEmptyState({
                    viewportBoundsElement: tablePanelBoundsElement,
                });
            }
            return (
                <TableNoSearchResultsEmptyState
                    onClearFilters={handleClearFilters}
                    viewportBoundsElement={tablePanelBoundsElement}
                />
            );
        }
        return null;
    }, [
        controller.renderTableNoDataEmptyState,
        rowsQuery.status,
        handleClearFilters,
        handleRetryLoadData,
        isFullscreenEngagementPanelOpen,
        isTableDataLoading,
        loadedData.length,
        tablePanelBoundsElement,
    ]);

    const tableConfig: TableConfig<T> = {
        rowKey: "id",
        columns,
        rowSelection: enableTableRowSelection && hasVisibleDataColumns ? "multiple" : "none",
        rowSelectionState: tableRowSelectionState,
        onRowSelectionChange: ({selectedRowIds}) => {
            controller.setTableSelectedRowIds(selectedRowIds);
        },
        columnReordering: true,
        columnOrder:
            controller.tableColumnConfig !== null
                ? hasVisibleDataColumns
                    ? enableTableRowSelection
                        ? ["__checkbox__", ...activeColumnIdsForTable]
                        : activeColumnIdsForTable
                    : activeColumnIdsForTable
                : undefined,
        onColumnOrderChange: (order) => {
            const normalizedOrder = [
                ...(enableTableRowSelection ? ["__checkbox__"] : []),
                ...order.filter((id) => id !== "__checkbox__" && id !== TABLE_ACTIONS_COLUMN_ID),
                ...(showActionsColumn ? [TABLE_ACTIONS_COLUMN_ID] : []),
            ];
            const dataColumnIdsOnly = normalizedOrder.filter(
                (id) => id !== "__checkbox__" && id !== TABLE_ACTIONS_COLUMN_ID,
            );
            const nextOrderedVisibleColumnIds = orderedVisibleColumnIdsWithActionsLast(
                dataColumnIdsOnly,
                showActionsColumn,
            );
            const defaultPinnedIds = columns
                .filter((column) => column.pinned === "left")
                .map((column) => column.id)
                .filter((id) => id !== "__checkbox__" && id !== TABLE_ACTIONS_COLUMN_ID);
            const existingPinnedIds = controller.tableColumnConfig?.pinnedLeftColumnIds ?? defaultPinnedIds;
            const nextPinnedIds = existingPinnedIds
                .filter((id) => nextOrderedVisibleColumnIds.includes(id))
                .filter((id) => id !== TABLE_ACTIONS_COLUMN_ID);
            controller.setTableColumnConfig({
                revisionId: generateUuid(),
                orderedVisibleColumnIds: nextOrderedVisibleColumnIds,
                pinnedLeftColumnIds: nextPinnedIds,
            });
        },
        keepPinnedLeftFirst: true,
        mobileUi: {isMobile, maxPinnedLeftColumns: 1},
        rowActionsConfig:
            showActionsColumn && hasTableRowActions ? controller.tableRowActions : undefined,
        onViewRow:
            showActionsColumn && hasReadPanel && !hasTableRowActions
                ? (row) => {
                      controller.setCurrentlyOpenRowEngagementPanel({
                          rowEngagementType: RowEngagementType.read,
                          rowEngagementPanelType: RowEngagementPanelType.sidebar,
                          rowEngagementData: row,
                      });
                  }
                : undefined,
        onEditRow:
            showActionsColumn && hasUpdatePanel && !hasTableRowActions
                ? (row) => {
                      controller.setCurrentlyOpenRowEngagementPanel({
                          rowEngagementType: RowEngagementType.update,
                          rowEngagementPanelType:
                              controller.updatePanelOpensInFullscreen === true
                                  ? RowEngagementPanelType.fullscreen
                                  : RowEngagementPanelType.sidebar,
                          rowEngagementData: row,
                      });
                  }
                : undefined,
        canEditRow: controller.canEditRow,
        renderInlineActions:
            showActionsColumn && hasAdditionalActionComponent && !hasTableRowActions
                ? (row: T) => controller.additionalActionComponent!(row)
                : undefined,
        columnResizing: true,
        // sorting: true,
        isLoading: isTableDataLoading,
        skeletonRowCount: 10,
        emptyState,
    };

    const selectedRows = useMemo(
        () => loadedData.filter((row: T) => controller.tableSelectedRowIds.includes(String(row.id))),
        [loadedData, controller.tableSelectedRowIds],
    );
    const canTransferSelectedRows =
        controller.onRequestTransferSelectedRows === undefined
            ? false
            : (controller.canTransferSelectedRows?.(selectedRows) ?? true);

    useEffect(() => {
        if (showCopyToast !== true) {
            return;
        }
        const timeoutId = setTimeout(() => setShowCopyToast(false), 3000);
        return () => clearTimeout(timeoutId);
    }, [showCopyToast]);

    const handleCopySelectedRows = async () => {
        if (selectedRows.length === 0) {
            return;
        }
        try {
            const copyableColumns = columns.filter((column) => column.id !== "__actions__" && column.id !== "__checkbox__");
            const headerRow = copyableColumns.map((column) => column.displayName);
            const bodyRows = selectedRows.map((row: T) =>
                copyableColumns.map((column) => {
                    const rawValue = column.valueGetter(row);
                    if (rawValue === null || rawValue === undefined) {
                        return "";
                    }
                    if (Array.isArray(rawValue)) {
                        return rawValue.map((item) => String(item)).join(", ");
                    }
                    if (
                        typeof rawValue === "string" ||
                        typeof rawValue === "number" ||
                        typeof rawValue === "boolean"
                    ) {
                        return String(rawValue);
                    }
                    try {
                        return JSON.stringify(rawValue);
                    } catch {
                        return String(rawValue);
                    }
                }),
            );
            const tsvPayload = [headerRow, ...bodyRows]
                .map((row) => row.join("\t"))
                .join("\n");
            await navigator.clipboard.writeText(tsvPayload);
            setShowCopyToast(true);
        } catch (error) {
            showFlag({
                title: "Copy failed",
                description: error instanceof Error ? error.message : "Unable to copy selected rows.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 6,
            });
        }
    };

    const commitDeleteRows = async (rowsToDelete: Array<T>, rowIdsToDelete: Array<string>) => {
        if (rowsToDelete.length === 0) {
            return;
        }
        try {
            const archivedJourney = rowsToDelete.some(
                (row) => isEventDataViewerRow(row) !== true,
            );
            const archiveResults = await Promise.all(
                rowsToDelete.map((row) => {
                    return archiveDataViewerRow({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl,
                        workspaceId: workspace.id,
                        workspace: workspace,
                        workspaceCapabilities,
                        row,
                        queryKeyPrefix: controller.queryKeyPrefix,
                        eventContainerStatusColumnConfig:
                            isEventDataViewerRow(row)
                                ? undefined
                                : eventContainerTemplates.find(
                                      (template) =>
                                          template.id ===
                                          (row as T & {templateId?: Uuid}).templateId,
                                  )?.configuration.statusColumnConfig,
                        eventTemplates: eventTemplates,
                    });
                }),
            );
            const failedArchiveResult = archiveResults.find((result) => result.success === false);
            if (failedArchiveResult !== undefined) {
                setOptimisticallyDeletedRowIds((prev) => prev.filter((id) => !rowIdsToDelete.includes(id)));
                showFlag({
                    title: "Archive failed",
                    description:
                        failedArchiveResult.error instanceof Error
                            ? failedArchiveResult.error.message
                            : "Unknown error",
                    appearance: "error",
                    isAutoDismiss: true,
                    autoDismissSeconds: 8,
                });
                return;
            }
            await Promise.all([rowsQuery.refetch(), controller.rowCountQuery.refetch()]);
            void invalidateOttopilotDataViewerQueries(queryClient, controller.queryKeyPrefix);
            if (archivedJourney === true) {
                void invalidateOttopilotDataViewerQueries(queryClient, [
                    EVENTS_MODULE_DATA_VIEWER_QUERY_KEY,
                    workspace.id,
                ]);
            }
        } finally {
            setIsDeletingSelectedRows(false);
        }
    };

    const handleDeleteSelectedRows = () => {
        if (selectedRows.length === 0 || isDeletingSelectedRows) {
            return;
        }
        if (hasPendingDeleteToast) {
            showFlag({
                title: "Delete in progress",
                description: "Please close or undo current delete first.",
                appearance: "warning",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
            return;
        }
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDeleteFromModal = () => {
        const rowsToDelete = selectedRows;
        if (rowsToDelete.length === 0) {
            setIsDeleteModalOpen(false);
            return;
        }
        const rowIdsToDelete = rowsToDelete.map((row: T) => String(row.id));
        setIsDeleteModalOpen(false);
        setIsDeletingSelectedRows(true);
        setHasPendingDeleteToast(true);
        setPendingDeletePayload({rowsToDelete, rowIdsToDelete});
        const timeoutId = setTimeout(() => {
            void handleCommitDelete();
        }, 3000);
        setPendingDeleteTimeoutId(timeoutId);
        controller.setTableSelectedRowIds([]);
        setOptimisticallyDeletedRowIds((prev) => Array.from(new Set([...prev, ...rowIdsToDelete])));
    };

    const handleUndoDelete = () => {
        if (pendingDeletePayload === null) {
            return;
        }
        setHasPendingDeleteToast(false);
        setIsDeletingSelectedRows(false);
        setOptimisticallyDeletedRowIds((prev) =>
            prev.filter((id) => !pendingDeletePayload.rowIdsToDelete.includes(id)),
        );
        if (pendingDeleteTimeoutId !== null) {
            clearTimeout(pendingDeleteTimeoutId);
            setPendingDeleteTimeoutId(null);
        }
        setPendingDeletePayload(null);
    };

    const handleCommitDelete = async () => {
        if (pendingDeletePayload === null) {
            return;
        }
        const payload = pendingDeletePayload;
        setHasPendingDeleteToast(false);
        setPendingDeletePayload(null);
        if (pendingDeleteTimeoutId !== null) {
            clearTimeout(pendingDeleteTimeoutId);
            setPendingDeleteTimeoutId(null);
        }
        await commitDeleteRows(payload.rowsToDelete, payload.rowIdsToDelete);
    };

    return (
        <div className="flex min-h-0 h-full w-full min-w-0 flex-col overflow-hidden">
            <div className={twJoin("flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden", horizontalInsetClass)}>
                {enableTableRowSelection && hasVisibleDataColumns && controller.tableSelectedRowIds.length > 0 ? (
                    <TableSelectionBar
                        selectedCount={controller.tableSelectedRowIds.length}
                        totalCount={loadedData.length}
                        onClear={() => controller.setTableSelectedRowIds([])}
                        onCopy={handleCopySelectedRows}
                        onTransfer={
                            controller.onRequestTransferSelectedRows === undefined
                                ? undefined
                                : () => {
                                      controller.onRequestTransferSelectedRows?.(selectedRows);
                                      controller.setTableSelectedRowIds([]);
                                  }
                        }
                        isTransferDisabled={canTransferSelectedRows !== true}
                        bulkActionMode={controller.tableSelectionBulkAction ?? "delete"}
                        onDelete={
                            controller.tableSelectionBulkAction === "restore" || controller.tableSelectionBulkAction === "none"
                                ? undefined
                                : handleDeleteSelectedRows
                        }
                        onRestore={
                            controller.tableSelectionBulkAction === "restore" &&
                            controller.onRequestRestoreSelectedRows !== undefined
                                ? () => {
                                      controller.onRequestRestoreSelectedRows?.(selectedRows);
                                      controller.setTableSelectedRowIds([]);
                                  }
                                : undefined
                        }
                        isDeleteDisabled={isDeletingSelectedRows || hasPendingDeleteToast}
                        isRestoreDisabled={isDeletingSelectedRows || hasPendingDeleteToast}
                    />
                ) : null}
                <div
                    ref={setTablePanelBoundsElement}
                    className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-solid border-ds-border bg-ds-elevation-surface-current"
                >
                    <GenericTable
                        data={tableRowData}
                        config={tableConfig}
                        className="table-container--fill !border-0 !rounded-none"
                    />
                </div>
            </div>
            {hidePageFooter !== true ? (
                <>
                    <div className="h-ds-space-250 w-full min-w-0 shrink-0" aria-hidden />
                    <div className="table-only-page-footer-border-target w-full shrink-0">
                        <div className="table-page-footer-bleed">
                            <TableFooter
                                className={horizontalInsetClass}
                                mode="paginated"
                                variant="page"
                                totalRows={totalRows}
                                totalRowsStatus={controller.rowCountQuery.status}
                                totalRowsError={
                                    controller.rowCountQuery.status === "error" ? controller.rowCountQuery.error : null
                                }
                                loadedRowCount={loadedData.length}
                                pageSize={pageSize}
                                onPageSizeChange={(size) => {
                                    controller.setPageSize(size);
                                }}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onFirstPage={() => controller.setPageNumber(1)}
                                onPreviousPage={() => controller.setPageNumber(Math.max(1, currentPage - 1))}
                                onNextPage={() => controller.setPageNumber(Math.min(totalPages, currentPage + 1))}
                                onLastPage={() => controller.setPageNumber(totalPages)}
                                onGoToPage={(page) => controller.setPageNumber(page)}
                                isFirstPage={currentPage <= 1}
                                isLastPage={currentPage >= totalPages}
                                isLoading={rowsQuery.isFetching === true}
                            />
                        </div>
                    </div>
                </>
            ) : null}
            {controller.tableSelectionBulkAction !== "restore" ? (
                <TableDeleteConfirmationModal
                    opened={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleConfirmDeleteFromModal}
                />
            ) : null}
            <TableOperationToasts
                showCopyToast={showCopyToast}
                onDismissCopyToast={() => setShowCopyToast(false)}
                showPendingDeleteToast={hasPendingDeleteToast}
                onDismissPendingDeleteToast={() => {
                    void handleCommitDelete();
                }}
                onUndoDelete={handleUndoDelete}
            />
        </div>
    );
}
